import type { FilhoEtapa2Candidato, RespostasEtapa2Candidato } from '../../models';
import {
  BANCO_ITAU_CODIGO,
  PLANO_SAUDE_NAO_ADERIR,
  escolaridadeTemCurso,
  montarEnderecoCompleto,
} from '../../models/candidato.opcoes';

export type EtapaCandidatoEtapa2 =
  | 'introducao'
  | 'dados_complementares'
  | 'contato_endereco'
  | 'trabalho'
  | 'beneficios_familia'
  | 'pessoal'
  | 'curiosidades'
  | 'documentos'
  | 'autorizacao';

export const ETAPAS_CANDIDATO_ETAPA2: EtapaCandidatoEtapa2[] = [
  'introducao',
  'dados_complementares',
  'contato_endereco',
  'trabalho',
  'beneficios_familia',
  'pessoal',
  'curiosidades',
  'documentos',
  'autorizacao',
];

/** Estado do wizard — igual a RespostasEtapa2Candidato, mas sem `documentos` (que aqui
 * fica só em memória como File, convertido pra base64 só no envio, ver
 * CandidatoFormExternoEtapa2Page). */
export type RespostasEtapa2Estado = Omit<RespostasEtapa2Candidato, 'documentos'>;

export const RESPOSTAS_ETAPA2_INICIAIS: RespostasEtapa2Estado = {
  genero: '',
  raca: '',
  sexo: '',
  orgaoEmissorRg: '',
  orgaoEmissorRgUf: '',
  dataEmissaoRg: '',
  sobrenomeEmail: '',
  nacionalidade: '',
  estadoCivil: '',
  nomeConjuge: '',
  possuiDeficiencia: false,
  deficienciaCid: '',
  escolaridade: '',
  escolaridadeStatus: '',
  escolaridadeCurso: '',
  telefone: '',
  emailPessoal: '',
  contatoEmergenciaNome: '',
  contatoEmergenciaTelefone: '',
  contatoEmergenciaParentesco: '',
  endereco: '',
  enderecoCep: '',
  enderecoLogradouro: '',
  enderecoNumero: '',
  enderecoComplemento: '',
  enderecoBairro: '',
  enderecoCidade: '',
  enderecoUf: '',
  enderecoPais: 'Brasil',
  tipoContrato: '',
  cnpj: '',
  razaoSocial: '',
  estagioPeriodo: '',
  estagioInstituicao: '',
  possuiContaItau: true,
  outroBancoCodigo: '',
  outroBancoAgencia: '',
  outroBancoConta: '',
  outroBancoDigito: '',
  outroBancoPix: '',
  regimeTrabalho: [],
  linhaOnibusTarifa: '',
  planoSaude: '',
  incluirConjugePlano: false,
  // Formato antigo de dependentes — não aparece mais no formulário, vai sempre vazio.
  desejaDependentesPlano: false,
  dependentesPlano: [],
  pronome: '',
  possuiApelido: false,
  apelido: '',
  tamanhoCamisa: '',
  tamanhoCalcado: '',
  possuiFilhos: false,
  filhos: [],
  possuiAlergia: false,
  alergiaDescricao: '',
  tipoAlimentacao: '',
  possuiRestricaoAlimentar: false,
  restricaoAlimentarDescricao: '',
  comidaBebidaFavorita: '',
  tempoLivre: '',
  lugarQuerVisitar: '',
  corFavorita: '',
  livroFavorito: '',
  filmeFavorito: '',
  personagemFavorito: '',
  bandaCantorFavorito: '',
  musicaDoMomento: '',
  praticaEsporte: '',
  talentoEspecial: '',
  metaSonho: '',
  possuiPet: false,
  petDescricao: '',
  interesseCompartilharFotos: false,
  autorizaUsoImagens: null,
  autorizacaoTermo: false,
};

export interface ArquivosEtapa2 {
  identidadeFrente: File | null;
  identidadeVerso: File | null;
  comprovanteEndereco: File | null;
  fotoRecente: File | null;
  certidaoCasamento: File | null;
  rgConjuge: File | null;
  rgMae: File | null;
  rgPai: File | null;
  laudoPcd: File | null;
}

export interface ArquivosFilhoEtapa2 {
  certidaoNascimento: File | null;
  rgIdentidade: File | null;
}

export const ARQUIVOS_FILHO_INICIAIS: ArquivosFilhoEtapa2 = { certidaoNascimento: null, rgIdentidade: null };

export const ARQUIVOS_ETAPA2_INICIAIS: ArquivosEtapa2 = {
  identidadeFrente: null,
  identidadeVerso: null,
  comprovanteEndereco: null,
  fotoRecente: null,
  certidaoCasamento: null,
  rgConjuge: null,
  rgMae: null,
  rgPai: null,
  laudoPcd: null,
};

export const FILHO_ETAPA2_INICIAL: FilhoEtapa2Candidato = {
  nome: '',
  dataNascimento: '',
  sexo: '',
  incluirNoPlano: false,
};

export const INPUT_CLASS =
  'w-full rounded-xl border-2 border-gray-100 p-3 focus:border-indigo-400 focus:outline-none';
export const LABEL_CLASS = 'mb-1 block text-sm font-semibold text-gray-700';

const preenchido = (valor: string | null | undefined) => Boolean(valor?.trim());

/** Mesma checagem usada por RespostasEtapa2CandidatoSchema (`.email()`) — validado aqui
 * também pra travar o "Próximo" já na etapa do e-mail, em vez de só descobrir no envio
 * final (decisão registrada: erro tem que aparecer perto de onde a pessoa errou). */
export const emailValido = (valor: string | null | undefined) => Boolean(valor && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor));

/** Evita passar só o código do país (ex. "+55") como telefone válido — exige DDD +
 * número (mesma checagem em RespostasEtapa2CandidatoSchema/submitCandidatoExternoEtapa2). */
export const telefoneValido = (valor: string | null | undefined) => Boolean(valor && valor.replace(/\D/g, '').length >= 12);

export const CASADO_OU_UNIAO_ESTAVEL = ['Casado(a)', 'União Estável'];

export const cepValido = (valor: string | null | undefined) => (valor ?? '').replace(/\D/g, '').length === 8;

/** Espelha (de forma simplificada, só pra liberar o botão "Próximo") os condicionais
 * validados de verdade em RespostasEtapa2CandidatoSchema/submitCandidatoExternoEtapa2. */
export function etapaEstaCompleta(etapa: EtapaCandidatoEtapa2, r: RespostasEtapa2Estado): boolean {
  switch (etapa) {
    case 'introducao':
      return true;
    case 'dados_complementares':
      return (
        preenchido(r.genero) &&
        preenchido(r.raca) &&
        preenchido(r.sexo) &&
        preenchido(r.orgaoEmissorRg) &&
        preenchido(r.orgaoEmissorRgUf) &&
        preenchido(r.dataEmissaoRg) &&
        preenchido(r.sobrenomeEmail) &&
        preenchido(r.nacionalidade) &&
        preenchido(r.estadoCivil) &&
        (!CASADO_OU_UNIAO_ESTAVEL.includes(r.estadoCivil ?? '') || preenchido(r.nomeConjuge)) &&
        telefoneValido(r.telefone) &&
        emailValido(r.emailPessoal) &&
        (!r.possuiDeficiencia || preenchido(r.deficienciaCid)) &&
        preenchido(r.escolaridade) &&
        preenchido(r.escolaridadeStatus) &&
        (!escolaridadeTemCurso(r.escolaridade) || preenchido(r.escolaridadeCurso))
      );
    case 'contato_endereco':
      return (
        preenchido(r.contatoEmergenciaNome) &&
        telefoneValido(r.contatoEmergenciaTelefone) &&
        preenchido(r.contatoEmergenciaParentesco) &&
        cepValido(r.enderecoCep) &&
        preenchido(r.enderecoLogradouro) &&
        preenchido(r.enderecoNumero) &&
        preenchido(r.enderecoBairro) &&
        preenchido(r.enderecoCidade) &&
        preenchido(r.enderecoUf) &&
        preenchido(r.enderecoPais)
      );
    case 'trabalho':
      return (
        preenchido(r.tipoContrato) &&
        (r.tipoContrato !== 'PJ' || (preenchido(r.cnpj) && preenchido(r.razaoSocial))) &&
        (r.tipoContrato !== 'Estagiário' ||
          (preenchido(r.estagioPeriodo) && preenchido(r.estagioInstituicao) && preenchido(r.escolaridadeCurso))) &&
        preenchido(r.outroBancoCodigo) &&
        r.outroBancoCodigo !== BANCO_ITAU_CODIGO &&
        preenchido(r.outroBancoAgencia) &&
        preenchido(r.outroBancoConta) &&
        preenchido(r.outroBancoDigito) &&
        preenchido(r.outroBancoPix) &&
        r.regimeTrabalho.length > 0 &&
        (r.regimeTrabalho.every((v) => v === 'Remoto') || preenchido(r.linhaOnibusTarifa))
      );
    case 'beneficios_familia':
      return (
        preenchido(r.planoSaude) &&
        (!r.possuiFilhos ||
          (r.filhos.length > 0 &&
            r.filhos.every((f) => preenchido(f.nome) && preenchido(f.dataNascimento) && preenchido(f.sexo))))
      );
    case 'pessoal':
      return (
        preenchido(r.pronome) &&
        (!r.possuiApelido || preenchido(r.apelido)) &&
        preenchido(r.tamanhoCamisa) &&
        preenchido(r.tamanhoCalcado) &&
        (!r.possuiAlergia || preenchido(r.alergiaDescricao)) &&
        preenchido(r.tipoAlimentacao) &&
        (!r.possuiRestricaoAlimentar || preenchido(r.restricaoAlimentarDescricao)) &&
        (!r.possuiPet || preenchido(r.petDescricao))
      );
    case 'curiosidades':
      return (
        preenchido(r.comidaBebidaFavorita) &&
        preenchido(r.tempoLivre) &&
        preenchido(r.corFavorita) &&
        preenchido(r.livroFavorito) &&
        preenchido(r.filmeFavorito) &&
        preenchido(r.personagemFavorito) &&
        preenchido(r.praticaEsporte) &&
        preenchido(r.talentoEspecial) &&
        preenchido(r.metaSonho) &&
        (!r.interesseCompartilharFotos || r.autorizaUsoImagens !== null)
      );
    case 'documentos':
      return true; // documentos são opcionais no wizard (RH complementa depois se faltar)
    case 'autorizacao':
      return r.autorizacaoTermo === true;
    default:
      return true;
  }
}

/**
 * Ajustes finais antes de enviar:
 * - `endereco` (texto completo) é montado das partes no padrão do RH — e continua indo
 *   porque uma function mais antiga ainda publicada exige esse campo;
 * - quem não vai aderir ao plano (ou não tem cônjuge) não leva marcação de dependente que
 *   tenha ficado de uma escolha anterior;
 * - lista antiga de dependentes vai sempre vazia (formato substituído).
 */
export function prepararRespostasParaEnvio(r: RespostasEtapa2Estado): RespostasEtapa2Estado {
  const aderePlano = Boolean(r.planoSaude) && r.planoSaude !== PLANO_SAUDE_NAO_ADERIR;
  const temConjuge = CASADO_OU_UNIAO_ESTAVEL.includes(r.estadoCivil ?? '') && Boolean(r.nomeConjuge?.trim());
  return {
    ...r,
    endereco: montarEnderecoCompleto(r),
    nomeConjuge: temConjuge ? r.nomeConjuge : null,
    incluirConjugePlano: aderePlano && temConjuge && r.incluirConjugePlano === true,
    filhos: r.possuiFilhos ? r.filhos.map((f) => ({ ...f, incluirNoPlano: aderePlano && f.incluirNoPlano === true })) : [],
    razaoSocial: r.tipoContrato === 'PJ' ? r.razaoSocial : null,
    desejaDependentesPlano: false,
    dependentesPlano: [],
  };
}
